import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../auth/AuthPage/UserContext';
import LoginGate from '../../common/LoginGate/LoginGate';
import ShopMap from '../../common/ShopMap/ShopMap';
import ConfirmDialog from '../../common/ConfirmDialog/ConfirmDialog';
import './ShopDashboard.css';

/**
 * Shop account home: profile (name, fees, address -> auto-geocoded pin),
 * activation status (payment stub), and the middleman trade queue.
 */
function ShopDashboard() {
    const { user } = useUser();
    const [shop, setShop] = useState(null);
    const [notShop, setNotShop] = useState(false);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveMsg, setSaveMsg] = useState(null);
    const [legs, setLegs] = useState([]);
    const [busyLeg, setBusyLeg] = useState(null);
    const [queueQuery, setQueueQuery] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [lightbox, setLightbox] = useState(null); // {image, name}
    const [dialog, setDialog] = useState(null);     // styled confirm
    const [notice, setNotice] = useState(null);     // styled alert

    const load = useCallback(async () => {
        try {
            const res = await fetch('/api/shops/mine', { credentials: 'include' });
            if (res.status === 404) { setNotShop(true); return; }
            const data = await res.json();
            setShop(data.shop);
            setForm({
                name: data.shop.name || '',
                description: data.shop.description || '',
                fee_info: data.shop.fee_info || '',
                address: data.shop.address || '',
            });
        } catch { setNotShop(true); }
    }, []);

    const loadLegs = useCallback(async () => {
        try {
            const res = await fetch('/api/shops/mine/trades', { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setLegs(data.legs || []);
            }
        } catch { }
    }, []);

    useEffect(() => { if (user) { load(); loadLegs(); } }, [user, load, loadLegs]);

    // Live queue: incoming packages / partner verifications appear
    // without refreshing
    useEffect(() => {
        if (!user) return;
        const id = setInterval(() => {
            if (!document.hidden) loadLegs();
        }, 10000);
        const onFocus = () => loadLegs();
        window.addEventListener('focus', onFocus);
        return () => {
            clearInterval(id);
            window.removeEventListener('focus', onFocus);
        };
    }, [user, loadLegs]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSaveMsg(null);
        try {
            const res = await fetch('/api/shops/mine', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(form),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Save failed.');
            setShop(data.shop);
            setSaveMsg('Saved. Pin updated on the map.');
        } catch (err) {
            setSaveMsg(err.message);
        } finally {
            setSaving(false);
        }
    };

    const advance = async (leg, action) => {
        setBusyLeg(leg.id);
        try {
            const res = await fetch(`/api/trade-requests/${leg.trade_id}/shipping/advance`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ legId: leg.id, action }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Action failed.');
            await loadLegs();
        } catch (err) {
            setNotice({ title: 'Action failed', message: err.message });
        } finally {
            setBusyLeg(null);
        }
    };

    if (!user) return <LoginGate title="Shop" message="Log in to manage your shop." />;
    if (notShop) return <div className="requests-container"><div className="requests-placeholder">This account doesn't have shop permissions. Shops are approved by an admin.</div></div>;
    if (!shop || !form) return <div className="requests-container"><div className="requests-placeholder">Loading your shop&hellip;</div></div>;

    // ---- Queue derivation: search filter + active/history split ----
    const q = queueQuery.trim().toLowerCase();
    const matchesQuery = (leg) => {
        if (!q) return true;
        const hay = [
            `#${leg.trade_id}`, String(leg.trade_id), leg.trade_code,
            leg.sender_name, leg.receiver_name,
            leg.dest_resolved, leg.dest_address,
            ...(leg.cards || []).map(c => c.name),
        ];
        return hay.some(v => v && String(v).toLowerCase().includes(q));
    };
    const isHistory = (leg) =>
        ['delivered', 'shop_rejected'].includes(leg.status) ||
        ['finished', 'cancelled'].includes(leg.trade_status);
    const activeLegs = legs.filter(l => !isHistory(l) && matchesQuery(l));
    const historyLegs = legs.filter(l => isHistory(l) && matchesQuery(l));

    // 4-stage journey through the shop: Received -> Verified -> Shipped -> Delivered
    const QUEUE_STEPS = [
        ['shop_received', 'Received'],
        ['shop_verified', 'Verified'],
        ['forwarded', 'Shipped'],
        ['delivered', 'Delivered'],
    ];
    // A package another middleman verified and sent here for pickup
    const PICKUP_STEPS = [
        ['forwarded', 'On its way'],
        ['at_pickup', 'Arrived'],
        ['delivered', 'Picked up'],
    ];
    const STATUS_ORDER = ['ready', 'shipped', 'shop_received', 'shop_verified', 'forwarded', 'at_pickup', 'delivered'];

    const rejectWithConfirm = (leg) => {
        setDialog({
            title: 'Reject this package?',
            message: 'Use this when the contents don\'t match the trade. The whole trade is scrapped and both traders are notified immediately.',
            confirmLabel: 'Reject package',
            danger: true,
            action: () => advance(leg, 'shop_rejected'),
        });
    };

    const renderQueueItem = (leg) => {
        const rejected = leg.status === 'shop_rejected';
        const tradeDead = leg.trade_status === 'cancelled';
        const inert = rejected || tradeDead;
        const otherIsShop = leg.other_leg_method === 'shop';
        const otherVerified = ['shop_verified', 'forwarded', 'at_pickup', 'delivered'].includes(leg.other_leg_status);
        const forwardBlocked = leg.status === 'shop_verified' && otherIsShop && !otherVerified;
        // Pickup legs end at the receiver's own middleman: here at the
        // counter, or another shop this one forwards the package to
        const pickupHere = leg.role === 'pickup';
        const toOtherShop = !pickupHere && leg.dest_type === 'shop' &&
            !!leg.dest_shop_id && leg.dest_shop_id !== leg.shop_id;
        const isPickup = leg.dest_type === 'shop' && !toOtherShop;
        const reached = STATUS_ORDER.indexOf(leg.status);

        return (
            <li key={leg.id} className={`shop-queue-item${rejected ? ' rejected' : ''}`}>
                <div className="shop-queue-head">
                    <strong>Trade {leg.trade_code || `#${leg.trade_id}`}</strong>
                    <span className={`shop-queue-status${rejected ? ' bad' : ''}`}>
                        {rejected ? 'rejected' : pickupHere ? 'pickup' : leg.status.replace(/_/g, ' ')}
                    </span>
                </div>

                {/* the journey through this shop */}
                <ol className="queue-steps">
                    {(pickupHere ? PICKUP_STEPS : QUEUE_STEPS).map(([key, label]) => {
                        const idx = STATUS_ORDER.indexOf(key);
                        const done = !rejected && reached >= idx;
                        const current = !rejected && !done && STATUS_ORDER.indexOf(leg.status) === idx - 1;
                        return (
                            <li key={key} className={done ? 'done' : current ? 'current' : ''}>
                                <span className="queue-step-dot">{done ? '✓' : ''}</span>
                                {label}
                            </li>
                        );
                    })}
                </ol>

                <div className="shop-queue-meta">
                    From <Link className="shop-queue-user" to={`/profile/${leg.sender_id}`}>{leg.sender_name}</Link>
                    {' '}→ to <Link className="shop-queue-user" to={`/profile/${leg.receiver_id}`}>{leg.receiver_name}</Link>
                </div>
                <div className="queue-card-tiles">
                    {(leg.cards || []).map((c, i) => (
                        <button
                            key={i}
                            className="queue-card-tile"
                            title={`${c.name}${c.setCode ? ` · ${c.setCode}` : ''}`}
                            onClick={() => c.image_path && setLightbox({ image: c.image_path, name: c.name })}
                        >
                            {c.image_path
                                ? <img src={c.image_path} alt={c.name} loading="lazy" />
                                : <span className="queue-card-noimg">?</span>}
                            {c.image_path && <span className="queue-tile-info" aria-hidden="true">i</span>}
                            <span className="queue-card-name">{c.name}</span>
                            <span className="queue-card-meta">
                                {c.setCode}{c.rarity ? ` · ${c.rarity}` : ''}{c.finish && c.finish !== 'nonfoil' ? ` · ${c.finish}` : ''}
                            </span>
                            {c.min_price != null && (
                                <span className="queue-card-price">${c.min_price.toFixed(2)}</span>
                            )}
                        </button>
                    ))}
                </div>
                {pickupHere ? (
                    <>
                        <div className="shop-queue-meta">
                            Verified by <strong>{leg.from_shop_name || 'the sending middleman'}</strong>, then shipped here.
                            {' '}Destination: <strong>customer pickup at your counter</strong> ({leg.receiver_name})
                        </div>
                        {!inert && !['forwarded', 'at_pickup', 'delivered'].includes(leg.status) && (
                            <p className="ship-hint">Not sent yet. {leg.from_shop_name || 'The sending middleman'} ships it once both sides are verified.</p>
                        )}
                        {!inert && leg.status === 'forwarded' && (
                            <button className="cta-gold shop-queue-btn" disabled={busyLeg === leg.id}
                                onClick={() => advance(leg, 'at_pickup')}>
                                Mark arrived
                            </button>
                        )}
                        {!inert && leg.status === 'at_pickup' && (
                            <>
                                <p className="ship-hint">Ready for pickup. Hold for {leg.receiver_name}.</p>
                                <button className="cta-gold shop-queue-btn" disabled={busyLeg === leg.id}
                                    onClick={() => advance(leg, 'delivered')}>
                                    Customer picked up
                                </button>
                            </>
                        )}
                        {leg.status === 'delivered' && (
                            <p className="ship-hint">✓ Picked up in person by {leg.receiver_name}.</p>
                        )}
                        {tradeDead && (
                            <p className="shop-queue-rejectnote">Trade cancelled. No further action needed on this package.</p>
                        )}
                    </>
                ) : (
                <>
                {leg.status !== 'ready' && isPickup && (
                    <div className="shop-queue-meta">
                        Destination: <strong>customer pickup at your counter</strong> ({leg.receiver_name})
                    </div>
                )}
                {leg.status !== 'ready' && toOtherShop && (
                    <div className="shop-queue-meta">
                        Forward to: <strong>{leg.dest_shop_name || 'the pickup middleman'}</strong>
                        {leg.dest_shop_address ? `, ${leg.dest_shop_address}` : ''} (pickup for {leg.receiver_name})
                    </div>
                )}
                {leg.status !== 'ready' && !isPickup && !toOtherShop && (leg.dest_resolved || leg.dest_address) && (
                    <div className="shop-queue-meta">
                        Forward to: <strong>{leg.dest_resolved || leg.dest_address}</strong>
                    </div>
                )}
                {!rejected && otherIsShop && !['forwarded', 'at_pickup', 'delivered'].includes(leg.status) && (
                    <div className={`shop-queue-crosscheck${otherVerified ? ' ok' : ''}`}>
                        {otherVerified
                            ? <>✓ Other side ({leg.other_shop_name || 'partner middleman'}) has verified</>
                            : <>Other side ({leg.other_shop_name || 'partner middleman'}): {leg.other_leg_status ? leg.other_leg_status.replace(/_/g, ' ') : 'pending'}. Forwarding unlocks when both middlemen have verified</>}
                    </div>
                )}

                {!inert && leg.status === 'ready' && (
                    <>
                        <p className="ship-hint">Waiting for {leg.sender_name} to drop off.</p>
                        <button className="cta-ghost shop-queue-btn" disabled={busyLeg === leg.id}
                            onClick={() => advance(leg, 'shipped')}>
                            Log customer drop-off
                        </button>
                    </>
                )}
                {!inert && leg.status === 'shipped' && (
                    <button className="cta-gold shop-queue-btn" disabled={busyLeg === leg.id}
                        onClick={() => advance(leg, 'shop_received')}>
                        Mark received
                    </button>
                )}
                {!inert && leg.status === 'shop_received' && (
                    <div className="shop-queue-btnrow">
                        <button className="cta-gold shop-queue-btn" disabled={busyLeg === leg.id}
                            onClick={() => advance(leg, 'shop_verified')}>
                            Verify contents
                        </button>
                        <button className="shop-queue-reject" disabled={busyLeg === leg.id}
                            onClick={() => rejectWithConfirm(leg)}>
                            Reject package
                        </button>
                    </div>
                )}
                {!inert && leg.status === 'shop_verified' && (
                    forwardBlocked
                        ? <button className="cta-gold shop-queue-btn" disabled>
                            {isPickup ? 'Pickup locked until the other middleman verifies' : 'Forward locked until the other middleman verifies'}
                        </button>
                        : isPickup
                            ? <>
                                <p className="ship-hint">Ready for pickup. Hold for {leg.receiver_name}.</p>
                                <button className="cta-gold shop-queue-btn" disabled={busyLeg === leg.id}
                                    onClick={() => advance(leg, 'delivered')}>
                                    Customer picked up
                                </button>
                            </>
                            : <button className="cta-gold shop-queue-btn" disabled={busyLeg === leg.id}
                                onClick={() => advance(leg, 'forwarded')}>
                                {toOtherShop ? `Mark shipped to ${leg.dest_shop_name || 'the pickup middleman'}` : 'Mark shipped to destination'}
                            </button>
                )}
                {!inert && ['forwarded', 'at_pickup'].includes(leg.status) && (
                    <p className="ship-hint">
                        {toOtherShop
                            ? `Shipped. ${leg.receiver_name} picks it up at ${leg.dest_shop_name || 'their middleman'}.`
                            : `Shipped. Waiting for ${leg.receiver_name} to confirm receipt.`}
                    </p>
                )}
                {leg.status === 'delivered' && (
                    <p className="ship-hint">
                        {isPickup
                            ? <>✓ Picked up in person by {leg.receiver_name}.</>
                            : toOtherShop
                                ? <>✓ Picked up by {leg.receiver_name} at {leg.dest_shop_name || 'their middleman'}.</>
                                : <>✓ Delivered and confirmed by {leg.receiver_name}.</>}
                    </p>
                )}
                {tradeDead && !rejected && (
                    <p className="shop-queue-rejectnote">
                        Trade cancelled. No further action needed on this package.
                    </p>
                )}
                {rejected && (
                    <p className="shop-queue-rejectnote">
                        You rejected this package. Both traders have been notified.
                        {leg.shop_rejected_at ? ` (${new Date(leg.shop_rejected_at).toLocaleDateString()})` : ''}
                    </p>
                )}
                </>
                )}
            </li>
        );
    };

    return (
        <div className="requests-container shopdash">
            <header className="page-section-head">
                <h1>Shop <span className="page-section-current">{shop.name}</span></h1>
            </header>

            <div className={`shop-status-banner ${shop.status}`}>
                {shop.status === 'active' && (
                    <>Active middleman{shop.paid_until ? ` · paid through ${new Date(shop.paid_until).toLocaleDateString()}` : ''} · ${Number(shop.monthly_fee).toFixed(0)}/mo</>
                )}
                {shop.status === 'pending_payment' && (
                    <>Awaiting activation. The shop fee is ${Number(shop.monthly_fee).toFixed(0)}/mo. Contact the site admin to activate your account.</>
                )}
                {shop.status === 'suspended' && (
                    <>Suspended. Contact the site admin.</>
                )}
            </div>

            {/* Profile is tucked away - the queue is the working view */}
            <button className="shop-profile-toggle" onClick={() => setShowProfile(p => !p)}>
                {showProfile ? '▾' : '▸'} Shop profile & location
            </button>

            <div className="shopdash-stack">
                <section className="ship-panel" style={{ display: showProfile ? undefined : 'none' }}>
                    <h3>Shop profile</h3>
                    <form className="shop-form" onSubmit={save}>
                        <label>Shop name
                            <input value={form.name} maxLength={120}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                        </label>
                        <label>Description
                            <textarea value={form.description} maxLength={500} rows={3}
                                placeholder="What you sell, hours, anything traders should know"
                                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                        </label>
                        <label>Middleman fees
                            <input value={form.fee_info} maxLength={300}
                                placeholder='e.g. "$5 flat per trade, free for purchases over $50"'
                                onChange={e => setForm(f => ({ ...f, fee_info: e.target.value }))} />
                        </label>
                        <label>Street address
                            <input value={form.address} maxLength={300}
                                placeholder="123 Main St, City, State"
                                onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                        </label>
                        <button className="cta-gold" type="submit" disabled={saving}>
                            {saving ? 'Saving…' : 'Save profile'}
                        </button>
                        {saveMsg && <p className="ship-hint">{saveMsg}</p>}
                    </form>
                    {shop.lat != null && (
                        <div className="ship-map-wrap">
                            <ShopMap shops={[shop]} height={220} />
                        </div>
                    )}
                </section>

                <section className="ship-panel">
                    <div className="shop-queue-headrow">
                        <h3>Middleman queue</h3>
                        <input
                            className="shop-queue-search"
                            placeholder="Search trade #, trader, card, address…"
                            value={queueQuery}
                            onChange={e => setQueueQuery(e.target.value)}
                        />
                    </div>

                    {activeLegs.length === 0 && (
                        <p className="ship-hint">
                            {q ? 'No active packages match your search.' : 'No packages in flight. Trades routed through your shop will appear here.'}
                        </p>
                    )}

                    <ul className="shop-queue">
                        {activeLegs.map(leg => renderQueueItem(leg))}
                    </ul>

                    <button
                        className="shop-history-toggle"
                        onClick={() => setShowHistory(h => !h)}
                    >
                        {showHistory ? '▾' : '▸'} History ({historyLegs.length})
                    </button>
                    {showHistory && (
                        historyLegs.length === 0
                            ? <p className="ship-hint">{q ? 'No history matches your search.' : 'Completed and rejected trades will collect here.'}</p>
                            : <ul className="shop-queue shop-queue-history">
                                {historyLegs.map(leg => renderQueueItem(leg))}
                            </ul>
                    )}
                </section>
            </div>

            {lightbox && (
                <div className="queue-lightbox" onClick={() => setLightbox(null)}>
                    <img src={lightbox.image} alt={lightbox.name} />
                    <span className="queue-lightbox-name">{lightbox.name} (click anywhere to close)</span>
                </div>
            )}

            <ConfirmDialog
                open={!!dialog}
                title={dialog?.title}
                message={dialog?.message}
                confirmLabel={dialog?.confirmLabel}
                danger={dialog?.danger}
                busy={busyLeg !== null}
                onConfirm={() => { const a = dialog.action; setDialog(null); a(); }}
                onCancel={() => setDialog(null)}
            />
            <ConfirmDialog
                open={!!notice}
                title={notice?.title}
                message={notice?.message}
                onCancel={() => setNotice(null)}
            />
        </div>
    );
}

export default ShopDashboard;
